/**
 * MoJ cluster — Wave 4 deep Q&A (service × use-case × intent).
 * Deterministic, price-free, self-contained answers for AI answer engines.
 */
import { MOJ_SERVICES } from "@/data/moj";
import { expandDeep, deepAsOf, type DeepAxis } from "@/data/_qa/deep-expander";
import type { MojQA } from "./questions-500";

const AS_OF = deepAsOf(
  "สิงหาคม 2569",
  "ข้อกำหนดของศาล กระทรวงยุติธรรม กรมการกงสุล และสถานทูตเปลี่ยนได้ ควรยืนยันกับหน่วยงานผู้รับเอกสารก่อนยื่นทุกครั้ง",
);

const USE_CASES: DeepAxis[] = [
  { slug: "court", th: "การยื่นต่อศาลไทย", note: "ต้องยื่นให้ทันกำหนดนัดและตรงตามระเบียบของศาล" },
  { slug: "foreign-court", th: "การใช้ในศาลต่างประเทศ", note: "มักต้องรับรองเพิ่มชั้นและใช้คำแปลที่ศาลปลายทางยอมรับ" },
  { slug: "embassy", th: "การยื่นต่อสถานทูต", note: "รูปแบบคำแปลและอายุเอกสารกำหนดโดยสถานทูตแต่ละแห่ง" },
  { slug: "consular", th: "การรับรองที่กรมการกงสุล", note: "ต้องรับรองลายมือชื่อผู้แปลให้ครบชั้นก่อนยื่น" },
  { slug: "immigration", th: "การยื่นต่อสำนักงานตรวจคนเข้าเมือง", note: "ต้องสอดคล้องกับเอกสารชุดที่เคยยื่นไว้ก่อนหน้า" },
  { slug: "business", th: "การใช้ในการทำสัญญาธุรกิจ", note: "ต้องตรวจอำนาจผู้ลงนามตามหลักฐานล่าสุด" },
  { slug: "registry", th: "การยื่นต่อสำนักงานทะเบียนหรือที่ว่าการอำเภอ", note: "นายทะเบียนอาจขอเอกสารเพิ่มตามข้อเท็จจริงของเรื่อง" },
  { slug: "apostille", th: "การขอ Apostille", note: "เอกสารและคำแปลต้องอยู่ในรูปแบบที่กรมการกงสุลรับได้" },
  { slug: "school", th: "การยื่นต่อสถานศึกษา", note: "ควรตรวจการสะกดชื่อให้ตรงกับเอกสารการศึกษาเดิม" },
  { slug: "insurance", th: "การเคลมประกันหรือสวัสดิการ", note: "ผู้รับอาจกำหนดอายุเอกสารและรูปแบบคำแปลเฉพาะ" },
];

const INTENTS: DeepAxis[] = [
  { slug: "lawyer", th: "ทนายความที่เตรียมเอกสารให้ลูกความ" },
  { slug: "individual", th: "บุคคลทั่วไปที่ยื่นเอง" },
  { slug: "company", th: "บริษัทหรือฝ่ายกฎหมายองค์กร" },
  { slug: "expat", th: "ชาวต่างชาติที่พำนักในไทย" },
  { slug: "urgent", th: "ผู้ที่มีกำหนดยื่นกระชั้น" },
];

export const MOJ_QA_WAVE4: MojQA[] = expandDeep<MojQA["cluster"]>({
  prefix: "moj-w4",
  asOf: AS_OF,
  authorityTh: "หน่วยงานผู้รับเอกสาร",
  clusterTh: "งานแปลรับรองกระทรวงยุติธรรม",
  primary: MOJ_SERVICES.map((s) => ({ slug: s.slug, th: s.nameTh, en: s.nameEn, note: `มักใช้กับ ${s.audience.slice(0, 2).join(" / ")} และถูกยอมรับโดย ${s.acceptedBy.slice(0, 2).join(" / ")}` })),
  secondary: USE_CASES,
  intents: INTENTS,
  clusters: { documents: "documents", process: "process", risk: "legal", location: "use-case" },
  cap: 430,
});
